import React, { useContext } from 'react'
import { useTranslation } from 'i18n'
import { StepWizardChildProps } from 'react-step-wizard'
import { FormProvider, useForm } from 'react-hook-form'
import { SStyled } from 'styles/theme'

import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import SBox from 'components/opinionless/SBox'
import { FHeading1 } from 'components/BaseComponents/Text'
import FTextArea from 'components/BaseComponents/FTextArea'
import FInput from 'components/BaseComponents/FInput'
import FButton from 'components/BaseComponents/FButton'

import { defaultMargin } from 'components/BaseComponents/constants'
import { ga4_useTitle } from 'utils/ga4'

import { useAddApplicationAnswersMutation } from 'types/graphql'

import { ApplicationContext } from 'v2/context/application'
import { useSendConfirmationMutation } from 'types/graphql'
import { useApplicationBasicInfo } from 'v2/hooks/useApplicationBasicInfo'

import style from './style.module.scss'

import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'

export const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
`

interface Props {
    personId: string
    setCurrentStep?: (number) => void
    fontFamily: any
    onNextStep?: any
    onPrevious?: any
    updatePercentage: any
}

const ReviewElectronicSignature: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const applicationContext = useContext(ApplicationContext)

    const { t } = useTranslation(window.LANG_NS)
    const form = useForm()
    const { errors, handleSubmit } = form
    const [addApplicationAnswers, { loading: addApplicationAnswersLoading }] = useAddApplicationAnswersMutation()
    const [sendConfirmation] = useSendConfirmationMutation()

    const requiredString = 'signed_application_name_error'

    const { refetch: refetchApplicationBasicInfo } = useApplicationBasicInfo()
    ga4_useTitle('Electronic signature')

    const saveAnswer = () => {
        addApplicationAnswers({
            variables: {
                answers: [
                    {
                        personApplicationId: applicationContext.application.id,
                        category: 'SIGN_VERIFICATION',
                        response: new Date().toISOString(),
                    },
                ],
            },
        }).then((response) => {
            if (response.data.addApplicationAnswers.success === true) {
                sendConfirmation({
                    variables: {
                        applicationDetailsId: applicationContext.application.id,
                    },
                }).then((response) => {
                    if (response.data.sendConfirmation.ok === true) {
                        refetchApplicationBasicInfo()
                        props.nextStep()
                    } else {
                        form.setError('review_electronic_signature', { type: 'oops', message: t('error') })
                    }
                })
            } else {
                form.setError('review_electronic_signature', { type: 'oops', message: t('error') })
            }
        })
    }

    const fontFamily = props.fontFamily

    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.review,
        WIZARD_STEPS_MAPPING.review.electronicSignature,
        WIZARD_STEPS_MAPPING
    )
    props.updatePercentage(percentage, wizardStep, childWizzardStep)
    return (
        <Container>
            <FormProvider {...form}>
                <SBox
                    as="form"
                    onSubmit={handleSubmit((values) => {
                        saveAnswer()
                    })}
                >
                    <FButtonPrevious onClick={props.previousStep} />
                    <FHeading1 text={t('long_beach_review_certification_header')} fontFamily={fontFamily.header} />
                    <FTextArea
                        label={t('please_read_following')}
                        paragraphs={[t('signing_below_applicant_declare'), t('certify_penalty_of_perjury')]}
                    />
                    <div className={style.household_applicant_review_applicant_name_container}>
                        <FInput
                            id="application_name"
                            name="application_name"
                            label={t('ssi_benefits_wizard_applicant_name_label')}
                            type="text"
                            readOnly={true}
                            fullParent={true}
                            placeholder={
                                applicationContext?.profile?.firstName + ' ' + applicationContext?.profile?.lastName
                            }
                        />
                    </div>
                    <div className={style.household_applicant_review_applicant_signature_container}>
                        <FInput
                            id="signed_application_name"
                            name="signed_application_name"
                            label={t('signature_full_name_label')}
                            type="text"
                            fullParent={true}
                            placeholder={t('type_full_name_label')}
                            ref={form.register({
                                required: requiredString,
                                validate: (value) => {
                                    return (
                                        value ===
                                            applicationContext?.profile?.firstName +
                                                ' ' +
                                                applicationContext?.profile?.lastName || requiredString
                                    )
                                },
                            })}
                        />
                    </div>

                    <FButton
                        id="review_electronic_signature"
                        buttonType={'filled'}
                        width={'100%'}
                        marginTop={Object.keys(errors).length > 0 ? '45px' : defaultMargin}
                        ga4_key={t('submit_application', { lang: 'en' })}
                        loading={addApplicationAnswersLoading ? true : false}
                    >
                        {t('submit_application')}
                    </FButton>
                </SBox>
            </FormProvider>
        </Container>
    )
}

export default ReviewElectronicSignature
